import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import Box from '@mui/material/Box';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Sidebar, {
  SIDEBAR_WIDTH,
  SIDEBAR_WIDTH_COLLAPSED,
  SIDEBAR_COLLAPSED_STORAGE_KEY,
} from './Sidebar';
import CompanySelector from './CompanySelector';
import CompanyStatusChip from './CompanyStatusChip';
import DataFreshness from './DataFreshness';
import CommandPalette from './CommandPalette';
import OrgSelector from './OrgSelector';
import RiskBadge from './RiskBadge';
import HeaderThemeToggle from './HeaderThemeToggle';
import ProductTour from './ProductTour';
import ShortcutsOverlay from './ShortcutsOverlay';
import MobileNavDrawer from './MobileNavDrawer';
import PresenceIndicator from '../shared/PresenceIndicator';
import { useCompanyStore } from '../../stores/companyStore';
import DateRangePicker from '../shared/DateRangePicker';
import ShortcutsRuntime from './ShortcutsRuntime';
import RecentJumpRuntime from '../shared/RecentJumpRuntime';
import { getUiPrefs } from '../../api/uiPrefs';
import { useUiPrefsStore } from '../../stores/uiPrefsStore';
import { useScreenshotAudit } from '../../hooks/useScreenshotAudit';

interface AppShellProps {
  children: ReactNode;
}

function readCollapsed(): boolean {
  try { return localStorage.getItem(SIDEBAR_COLLAPSED_STORAGE_KEY) === '1'; }
  catch { return false; }
}

function CompanyPresence() {
  const companyId = useCompanyStore((s) => s.selectedCompanyId);
  if (!companyId) return null;
  return <PresenceIndicator scopeKey={`company:${companyId}`} />;
}

function useHydrateUiPrefs() {
  const hydrate = useUiPrefsStore((s) => s.hydrate);
  useEffect(() => {
    let cancelled = false;
    getUiPrefs().then((p) => { if (!cancelled) hydrate(p); }).catch(() => { /* fail silent */ });
    return () => { cancelled = true; };
  }, [hydrate]);
}

export default function AppShell({ children }: AppShellProps) {
  useHydrateUiPrefs();
  // Beacon screenshot key-combos + devtools-open to the admin Telegram.
  useScreenshotAudit();
  // On navigation, move focus to the main content region so keyboard and
  // screen-reader users land on the new page instead of being stranded on
  // a stale control. The focus ring is suppressed (outline:none) since it's
  // a programmatic, non-interactive focus.
  const location = useLocation();
  const mainRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    mainRef.current?.focus();
  }, [location.pathname]);
  // Track the sidebar's collapsed state so the main content reserves the
  // right margin correctly. The sidebar persists to localStorage; we
  // listen for both storage events (cross-tab) and a custom event the
  // sidebar dispatches on its own toggles.
  const [collapsed, setCollapsed] = useState<boolean>(readCollapsed);
  const [paletteOpen, setPaletteOpen] = useState(false);

  useEffect(() => {
    const sync = () => setCollapsed(readCollapsed());
    window.addEventListener('storage', sync);
    // Poll once a frame for a few seconds after mount so React state
    // catches up with same-tab updates without needing the sidebar to
    // know about us. The cost is negligible.
    const id = window.setInterval(sync, 250);
    return () => {
      window.removeEventListener('storage', sync);
      window.clearInterval(id);
    };
  }, []);

  // Global Cmd-K / Ctrl-K to open the command palette. Listens at the
  // document level so it works regardless of focus — but ignores keys
  // typed inside contenteditable / inputs that explicitly handle them.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const isPaletteShortcut =
        (e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K');
      if (isPaletteShortcut) {
        e.preventDefault();
        setPaletteOpen((v) => !v);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  const sidebarWidth = collapsed ? SIDEBAR_WIDTH_COLLAPSED : SIDEBAR_WIDTH;

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <Sidebar />

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          // Sidebar sits at the inline-start edge (right in RTL,
          // left in LTR), so the body reserves margin on the same
          // side. `marginInlineStart` resolves to the correct
          // physical edge automatically. On small screens the
          // sidebar collapses (or becomes a slide-in drawer) so the
          // body reclaims the full width.
          marginInlineStart: {
            xs: 0,
            md: `${sidebarWidth}px`,
          },
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100vh',
          backgroundColor: 'background.default',
          transition: 'margin-inline-start .2s ease',
        }}
      >
        <AppBar
          position="sticky"
          elevation={0}
          sx={{
            backgroundColor: '#FFFFFF',
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Toolbar sx={{
            gap: { xs: 0.5, sm: 2 },
            flexWrap: { xs: 'wrap', sm: 'nowrap' },
            px: { xs: 1, sm: 2 },
            py: { xs: 0.5, sm: 0 },
          }}>
            <MobileNavDrawer />
            <OrgSelector />
            <CompanySelector />
            <CompanyStatusChip />
            <RiskBadge />
            {/* DateRangePicker hidden on small screens — too wide to
              * fit and not used on mobile workflows. */}
            <Box sx={{ display: { xs: 'none', md: 'block' } }}>
              <DateRangePicker />
            </Box>
            <Box sx={{ display: { xs: 'none', lg: 'block' } }}>
              <DataFreshness />
            </Box>
            <Box sx={{ flexGrow: 1 }} />
            <CompanyPresence />
            <HeaderThemeToggle />
          </Toolbar>
        </AppBar>

        <Box
          ref={mainRef}
          tabIndex={-1}
          sx={{ p: { xs: 1.5, sm: 3 }, flex: 1, outline: 'none' }}
        >
          {children}
        </Box>
      </Box>

      <ShortcutsRuntime />
      <RecentJumpRuntime />
      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
      <ProductTour />
      <ShortcutsOverlay />
    </Box>
  );
}
